/**
 * Ena integracijska dolžnost do DS (T5 §5).
 *
 * ds/js/overlays.js veže swipe vrstice in bottom sheete na element ob
 * DOMContentLoaded. Vsak render, ki vstavi nove vrstice, jih pusti neaktivne,
 * dokler ne pokličemo overlaysInit() znova. Modul to izrecno izpostavlja
 * prav za ta namen.
 *
 * Pokliči rewireDs() iz vsake naročnine na store, ki spremeni seznam.
 */

interface NesylabGlobal {
  overlaysInit?: () => void;
  /** `ds/js/cmb.js`, od DS v4.20.10 (D2 §29). Idempotenten. */
  cmbInit?: () => void;
  a11y?: {
    announce: (msg: string, opts?: { assertive?: boolean }) => void;
    trapFocus: (target: Element | string) => () => void;
  };
  theme?: { toggle: () => void; current: () => string };
  /** `ds/js/menu.js`, od DS v4.23.0 (D2 §42). */
  menu?: { open(anchor: Element): void; close(anchor: Element): void; closeAll(): void };
  /** `ds/js/overlays.js`, od DS v4.23.0 (D2 §45). */
  sheet?: { open(id: string, trigger?: Element | null): void; close(id: string): void };
  /**
   * `ds/js/reorder.js`, od DS v4.26.0 (D2 §49–§55). Idempotenten: klic po
   * vsakem izrisu pripne poteg na nove vrstice, poslušalce seznama pa le enkrat.
   * `onReorder` je pot za shrambo, `announce` prevede naznanilo — ločeni pot,
   * da naznanilo ne postane skrit zapisovalnik (§52).
   */
  reorder?: {
    init(
      list: Element | string,
      opts?: {
        onReorder?: (item: Element, from: number, to: number) => void;
        announce?: (item: Element, to: number, total: number) => string;
      },
    ): void;
  };
}

declare global {
  interface Window {
    nesylab?: NesylabGlobal;
  }
}

export function rewireDs(): void {
  window.nesylab?.overlaysInit?.();
}

/**
 * Poveže preurejanje na `.reorder` seznam (`ds/js/reorder.js`, D2 §49–§55).
 *
 * Ista dolžnost kot `rewireDs()`, za način urejanja: modul ročice indeksira ob
 * DOMContentLoaded, naše vrstice pa nastanejo šele ob vklopu urejanja in se
 * prezidajo ob vsakem premiku. Klic je idempotenten (§54): poslušalce seznama
 * pripne enkrat, poteg na nove vrstice ob vsakem klicu.
 *
 * `onReorder` je pot za shrambo (`Station.order`), `announce` prevede naznanilo
 * v jezik aplikacije. **Ločeni** — modul naznanilo sme izpustiti ali podvojiti,
 * ne da bi se zapis izgubil (§52); zato zapisa ne obešamo na `announce`.
 */
export function initReorder(
  list: Element,
  opts: {
    onReorder: (item: Element, from: number, to: number) => void;
    announce: (item: Element, to: number, total: number) => string;
  },
): void {
  window.nesylab?.reorder?.init(list, opts);
}

/**
 * Poveže combobokse po prezidavi seznama izbir (`ds/js/cmb.js`, D2 §29).
 *
 * Ista dolžnost kot `rewireDs()`, samo za drugo komponento: modul indeksira
 * `[data-cmb]` ob nalaganju, naše izbire pa pridejo z omrežjem — 241 držav
 * nastane šele, ko katalog odgovori. Klic je idempotenten.
 *
 * ⚠️ **Kliči ga po vsaki zamenjavi `.cmb-opt` vozlišč, ne ob vsakem izrisu.**
 * `indexRoot()` vsaki izbiri dodeli `id` in prebere `data-label`; brez novega
 * klica nove izbire nimajo ne enega ne drugega in tipkovnica jih ne najde.
 */
export function rewireCombobox(): void {
  window.nesylab?.cmbInit?.();
}

export function announce(message: string, assertive = false): void {
  window.nesylab?.a11y?.announce(message, { assertive });
}

/**
 * Zapre vse odprte kebab menije.
 *
 * `ds/js/menu.js` meni **namerno** pusti odprt ob kliku vanj — pri meniju z
 * več izbirami je to pravilno. Pri nas je vsaka postavka končno dejanje, in
 * kadar odpre modal, meni ostane odprt **nad njim**: na WebKitu prestreza
 * klike na gumbe modala, na drugih pa vsaj visi čez.
 *
 * Zapiranje je zato naša dolžnost, ne pomanjkljivost DS-ja: DS ne more
 * vedeti, ali je postavka končna ali vmesna.
 *
 * ✅ **Od DS v4.23.0 (`3bab5e6`) gre to skozi javno pot** ([D2 §42](../../docs/04-delivery/D2-ds-requests.md)).
 * Prej je bilo zapiranje pri nas samo atribut, `menu.js` pa je ob odprtju
 * postavil pozicionirnik in ga podrl le v svojem `close()` — par `scroll`
 * (capture) in `resize` poslušalcev je zato ostal ob vsakem odprtju. Zdaj je
 * `data-open` edini vir resnice in ga modul opazuje sam.
 *
 * Nadomestna pot obstaja za okolje brez DS (enotni testi): tam ni ne panela
 * ne pozicionirnika, atribut pa mora vseeno slediti stanju.
 */
export function closeMenus(): void {
  const api = window.nesylab?.menu;
  if (api !== undefined) {
    api.closeAll();
    return;
  }
  for (const anchor of document.querySelectorAll<HTMLElement>('.menu-anchor[data-open="true"]')) {
    anchor.setAttribute('data-open', 'false');
    anchor.querySelector('[data-menu]')?.setAttribute('aria-expanded', 'false');
  }
}

/**
 * Odpre deljeni bottom sheet (`ds/js/overlays.js`, DS v4.23.0).
 *
 * ⚠️ **Vsebino napolni PRED tem.** Sheet je en element in jo dobi sproti;
 * `nesylab.sheet.open()` samo odpre in ujame fokus. `trigger` je element, ki
 * mu DS vrne fokus ob zaprtju — mora biti fokusabilen.
 */
export function openSheet(id: string, trigger: Element): void {
  window.nesylab?.sheet?.open(id, trigger);
}

/**
 * Ujame fokus v modalu in vrne funkcijo, ki ga izpusti.
 *
 * DS to zna (`ds/js/a11y.js`); lastna izvedba bi bila druga implementacija
 * istega pravila in bi se sčasoma razšla. Kadar DS ni naložen (enotni testi
 * v jsdom), vrne prazno funkcijo — modal je uporaben tudi brez pasti.
 */
export function trapFocus(target: Element): () => void {
  return window.nesylab?.a11y?.trapFocus(target) ?? (() => {});
}
